<template>
  <v-expand-transition>
    <div v-if="visible" class="murphy-search-panel">
      <div class="murphy-row">
        <span class="murphy-logo">
          <murphy-logo variant="logo" />
        </span>

        <!-- Tabs stacked -->
        <div class="murphy-tabs-col">
          <button
            :class="['murphy-tab', tab === 0 ? 'murphy-tab--active' : '']"
            @click="tab = 0"
          >
            <v-icon x-small class="mr-1">mdi-car</v-icon>Vehículos
          </button>
          <button
            :class="['murphy-tab', tab === 1 ? 'murphy-tab--active' : '']"
            @click="tab = 1"
          >
            <v-icon x-small class="mr-1">mdi-account</v-icon>Personas
          </button>
        </div>

        <!-- Vehículos fields -->
        <template v-if="tab === 0">
          <v-text-field
            v-model="placa"
            placeholder="Placa"
            dense outlined hide-details
            class="murphy-input"
            :loading="loading" :disabled="loading"
            @keyup.enter="buscarVehiculo"
          />
          <v-text-field
            v-model="serie"
            placeholder="Serie"
            dense outlined hide-details
            class="murphy-input"
            :loading="loading" :disabled="loading"
            @keyup.enter="buscarVehiculo"
          />
        </template>

        <!-- Personas fields -->
        <template v-if="tab === 1">
          <v-text-field
            v-model="nombre"
            placeholder="Nombre(s)"
            dense outlined hide-details
            class="murphy-input"
            :loading="loading" :disabled="loading"
            @keyup.enter="buscarPersona"
          />
          <v-text-field
            v-model="ap1"
            placeholder="Ap. Paterno"
            dense outlined hide-details
            class="murphy-input"
            :loading="loading" :disabled="loading"
            @keyup.enter="buscarPersona"
          />
          <v-text-field
            v-model="ap2"
            placeholder="Ap. Materno"
            dense outlined hide-details
            class="murphy-input"
            :loading="loading" :disabled="loading"
            @keyup.enter="buscarPersona"
          />
        </template>

        <!-- Search button -->
        <v-btn
          small
          color="var(--kln-accent)"
          :loading="loading"
          :disabled="loading"
          class="murphy-btn"
          data-tip="Buscar"
          @click="tab === 0 ? buscarVehiculo() : buscarPersona()"
        >
          <v-icon small>mdi-magnify</v-icon>
        </v-btn>

      </div>

      <!-- Error message -->
      <div v-if="error" class="murphy-error">
        <v-icon x-small color="error" class="mr-1">mdi-alert-circle</v-icon>
        {{ error }}
      </div>
    </div>
  </v-expand-transition>
</template>

<script>
import { mapGetters } from 'vuex'
import { MurphyService } from '@/util/murphy.service'
import MurphyLogo from '@/components/map-v2/utils/MurphyLogo.vue'
import {
  extractVehiculoFromMurphyResult,
  extractLprDataFromMurphyResult,
} from '@/components/map-v2/data/murphy-vehiculo-extractor'

export default {
  name: 'MurphyPanel',

  components: { MurphyLogo },

  props: {
    visible: { type: Boolean, default: false },
  },

  data: function() {
    return {
      tab: 0,
      loading: false,
      error: '',
      // Vehículos
      placa: '',
      serie: '',
      // Personas
      nombre: '',
      ap1: '',
      ap2: '',
    }
  },

  created: function() {
    // TEMP BYPASS: ensure murphy permissions exist for development
    var user = this.$store.state.auth.user
    if (user && user.attributes) {
      if (!user.attributes.permission_murphy) user.attributes.permission_murphy = true
      if (!user.attributes.consulta_murphy) user.attributes.consulta_murphy = 'dev_bypass'
    }
  },

  methods: {
    buscarVehiculo: function() {
      if (this.serie) {
        this._buscar('VehiculosSerie', { placa: this.serie }, 'vehiculo')
      } else if (this.placa) {
        this._buscar('VehiculosPlaca', { placa: this.placa }, 'vehiculo')
      }
    },

    buscarPersona: function() {
      if (!this.nombre && !this.ap1) return
      this._buscar('Personas', {
        nombre: this.nombre,
        apellido_1: this.ap1,
        apellido_2: this.ap2,
      }, 'persona')
    },

    // API pública: permite a componentes externos (SOSPanel, drawer de hit)
    // pre-llenar los campos y disparar la búsqueda sin tener que interactuar
    // con los inputs. El caller debe asegurar que el panel esté visible
    // (commit SET_PANEL murphyBuscador=true) antes de invocar.
    prefillAndSearch: function(kind, params) {
      var p = params || {}
      if (kind === 'persona') {
        this.tab = 1
        this.nombre = p.nombre || ''
        this.ap1 = p.ap1 || ''
        this.ap2 = p.ap2 || ''
        this.buscarPersona()
      } else if (kind === 'vehiculo') {
        this.tab = 0
        this.placa = p.placa || ''
        this.serie = p.serie || ''
        this.buscarVehiculo()
      }
    },

    _buscar: function(queryName, params, type) {
      var self = this
      var murphyUser = this.getUser && this.getUser.attributes
        ? this.getUser.attributes.consulta_murphy
        : null

      if (!murphyUser) {
        this.error = 'No tienes permisos para realizar esta consulta'
        return
      }

      this.loading = true
      this.error = ''

      var murphyService = new MurphyService()
      murphyService.createQuery(queryName, murphyUser, params).then(function(result) {
        if (!result.queryObjects || result.queryObjects.length === 0) {
          self.error = 'Sin resultados'
          return
        }

        if (type === 'vehiculo') {
          // Construye {vehiculoData, roboData, placasAsociadas} desde el
          // resultado crudo, priorizando por fuente y sanitizando placeholders
          // como "undefined" / "SIN INFORMACION". Antes hacíamos
          // `result.queryObjects[0].properties` con un Object.assign-ish
          // destructivo que perdía info — ver murphy-vehiculo-extractor.js.
          var extracted = extractVehiculoFromMurphyResult(result.queryObjects)
          // ALPR a veces cae en una fila post-join distinta porque su
          // serie/vehiculo vienen como placeholder y Utility.join hashea
          // por glueProperties. Recorremos TODAS las filas para no perder
          // detecciones — ver extractLprDataFromMurphyResult.
          var lprData = extractLprDataFromMurphyResult(result.queryObjects)
          self.$emit('murphy-result', {
            type: 'vehiculo',
            data: extracted.vehiculoData,
            roboData: extracted.roboData,
            placasAsociadas: extracted.placasAsociadas,
            lprData: lprData,
          })
        } else {
          var personas = []
          result.queryObjects.forEach(function(obj) {
            personas.push(obj.properties)
          })
          self.$emit('murphy-result', { type: 'persona', personas: personas })
        }
      }).catch(function(err) {
        console.error('[Murphy]', err)
        self.error = 'Error en la consulta'
      }).finally(function() {
        self.loading = false
      })
    },
  },

  computed: {
    ...mapGetters('auth', ['getUser']),
  },
}
</script>

<style scoped>
.murphy-search-panel {
  padding: 4px 8px 4px;
  border-top: 1px solid var(--kln-border-dim);
}

.murphy-row {
  display: flex;
  align-items: stretch;
  gap: 4px;
}

.murphy-tabs-col {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex-shrink: 0;
}

.murphy-tab {
  display: flex;
  align-items: center;
  padding: 0 8px;
  height: 18px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 600;
  color: var(--kln-text-dim);
  background: transparent;
  border: 1px solid var(--kln-border-dim);
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;
}

.murphy-tab:hover {
  color: var(--kln-text-muted);
  border-color: var(--kln-border-normal);
}

.murphy-tab--active {
  color: var(--kln-text);
  background: var(--kln-accent-soft);
  border-color: var(--kln-accent);
}

.murphy-logo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  line-height: 0;
}

.murphy-input {
  font-size: 12px !important;
  flex: 1;
  min-width: 0;
  max-width: 140px;
}

.murphy-input >>> .v-input__slot {
  min-height: 40px !important;
  padding: 0 8px !important;
}

.murphy-input >>> input {
  font-size: 12px !important;
  padding: 2px 0 !important;
}

.murphy-btn {
  min-width: 36px !important;
  height: 40px !important;
  padding: 0 6px !important;
  flex-shrink: 0;
}

.murphy-error {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: var(--kln-alert-danger);
  padding: 2px 2px 0;
}
</style>
